import { AddContactModal } from "@/components/add/AddContactModal";
import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "wouter";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { 
  Search, 
  Building2, 
  User, 
  Briefcase, 
  Users,
  ArrowRight,
  Clock,
  X
} from "lucide-react";
import { useGlobalSearch, type SearchResult } from "@/hooks/useGlobalSearch";
import { QueryError } from "@/components/QueryError";

interface GlobalSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GlobalSearch({ open, onOpenChange }: GlobalSearchProps) {
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [, setLocation] = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { results, isSearching: isLoading, isError, error, refetch, isFetching } = useGlobalSearch(query, open);

  // Recent searches (stored in localStorage)
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem("ftc_recent_searches");
    if (stored) {
      try { const parsed = JSON.parse(stored); if (Array.isArray(parsed)) setRecentSearches(parsed.filter(s => typeof s === "string")); } catch { /* Ignore invalid saved history. */ }
    }
  }, []);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query, results.length]);

  // Keep the highlighted row visible. Arrow keys moved the selection without
  // scrolling to it, so past the fold you were navigating something you could
  // not see. "nearest" scrolls the minimum needed and does nothing when the
  // row is already on screen.
  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-result-index="${selectedIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  // Reset the scroll position when the query changes, so a new search starts
  // at the top rather than wherever the last one was left.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [query]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => 
        prev < results.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (!isLoading && !isError && query.trim().length >= 2 && results[selectedIndex]) {
        selectResult(results[selectedIndex]);
      }
    } else if (e.key === "Escape") {
      onOpenChange(false);
    }
  }, [results, selectedIndex, onOpenChange, isLoading, isError, query]);

  const selectResult = (result: SearchResult) => {
    // Save to recent searches
    const newRecent = [result.name, ...recentSearches.filter(s => s !== result.name)].slice(0, 5);
    setRecentSearches(newRecent);
    localStorage.setItem("ftc_recent_searches", JSON.stringify(newRecent));
    
    // Navigate
    setLocation(result.href);
    onOpenChange(false);
    setQuery("");
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "company":
      case "buyer":
        return Building2;
      case "contact":
        return User;

      case "coi":
        return Users;
      default:
        return Building2;
    }
  };

  /**
   * Light and dark, because these were light-only.
   *
   * A -100 background with -700 text on a dark surface is a bright slab with
   * text that has almost no contrast against it, and this map paints both the
   * icon tile and the badge on every row, so the whole list was affected.
   * The dark values follow the convention already used in TasksWidget and
   * Meetings: a translucent -900 fill with -400 text.
   *
   * The fallback uses theme tokens rather than a fixed gray, so an unknown
   * result type inherits whatever the current theme is instead of guessing.
   */
  const getTypeColor = (type: string) => {
    switch (type) {
      case "buyer":
        return "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-900";
      case "company":
        return "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-900";
      case "contact":
        return "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-900";
      case "deal":
        return "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-900";
      case "coi":
        return "bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-400 dark:border-cyan-900";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "buyer":
        return "Buyer";
      case "company":
        return "Company";
      case "contact":
        return "Contact";

      case "coi":
        return "COI";
      default:
        return type;
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        Flex column with a real max-height, so the input and footer stay put
        and only the middle scrolls. DialogContent is `grid` by default and had
        no height cap at all, which is what let a long result list run off the
        bottom of the screen. cn() is tailwind-merge, so `flex` here correctly
        overrides the base `grid`.
      */}
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden flex flex-col max-h-[80vh]">
        <DialogTitle className="sr-only">Global Search</DialogTitle>

        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-border shrink-0">
          <Search className="w-5 h-5 text-muted-foreground" />
          <Input
            ref={inputRef}
            aria-label="Search companies and contacts"
            placeholder="Search companies, contacts..."
            className="flex-1 border-0 bg-transparent text-lg placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:ring-offset-0"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <kbd className="hidden sm:inline-flex h-6 select-none items-center gap-1 rounded border bg-muted px-2 font-mono text-xs font-medium text-muted-foreground">
            ESC
          </kbd>
        </div>

        {/*
          A plain overflow container, not <ScrollArea>.
          Radix's ScrollArea puts `overflow-hidden` on its root and `h-full` on
          its viewport. Given only a max-height and no definite height, that
          percentage resolves against an auto-height parent and falls back to
          auto, so the viewport grew to the full content height and the root
          simply clipped it: no scrollbar, no wheel scrolling, results cut off.
          A block with overflow-y-auto scrolls correctly at max-height, which
          here comes from flex-1 inside the capped dialog.
          overscroll-contain stops the page behind the dialog scrolling once
          the list hits its end.
        */}
        <div
          ref={listRef}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
        >
          {query.length < 2 ? (
            // Show recent searches or suggestions
            <div className="p-4">
              {recentSearches.length > 0 && (
                <>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    Recent Searches
                  </h4>
                  <div className="space-y-1">
                    {recentSearches.map((search, i) => (
                      <button
                        key={i}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-foreground hover:bg-muted transition-colors min-w-0"
                        onClick={() => setQuery(search)}
                      >
                        <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span className="truncate min-w-0">{search}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
              
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 mt-6">
                Quick Actions
              </h4>
              <div className="space-y-1">
                <button
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-foreground hover:bg-muted transition-colors"
                  onClick={() => {
                    setAddContactOpen(true);
                    onOpenChange(false);
                  }}
                >
                  <User className="w-4 h-4 text-muted-foreground shrink-0" />
                  Add New Contact
                </button>
                <button
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-foreground hover:bg-muted transition-colors"
                  onClick={() => {
                    setLocation("/buyers");
                    onOpenChange(false);
                  }}
                >
                  <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
                  View Buyer Network
                </button>
              </div>
            </div>
          ) : isError ? (
            <QueryError error={error} onRetry={() => refetch()} busy={isFetching} noun="search results" />
          ) : isLoading ? (
            <div className="p-4 space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-3 rounded-lg">
                  <div className="w-10 h-10 rounded-lg bg-muted animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="w-32 h-4 bg-muted rounded animate-pulse" />
                    <div className="w-20 h-3 bg-muted rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : results.length === 0 ? (
            <div className="p-8 text-center">
              <Search className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm font-medium text-foreground">No results found</p>
              <p className="text-xs text-muted-foreground mt-1">
                Try searching for a different term
              </p>
            </div>
          ) : (
            <div className="p-2">
              {results.map((result, index) => {
                const Icon = getTypeIcon(result.type);
                const isSelected = index === selectedIndex;
                
                return (
                  <button
                    key={`${result.type}-${result.id}`}
                    data-result-index={index}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-all",
                      isSelected 
                        ? "bg-primary/10 border border-primary/20" 
                        : "hover:bg-muted border border-transparent"
                    )}
                    onClick={() => selectResult(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
                      getTypeColor(result.type)
                    )}>
                      <Icon className="w-5 h-5" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      {/*
                        min-w-0 on this row too: a flex item defaults to
                        min-width:auto, so without it the name refuses to
                        shrink below its content and `truncate` never fires.
                        The badge is shrink-0 for the same reason in reverse,
                        or a long name squashes it until its label wraps.
                      */}
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-medium text-foreground truncate min-w-0">
                          {result.name}
                        </span>
                        <Badge variant="outline" className={cn("text-[10px] shrink-0", getTypeColor(result.type))}>
                          {getTypeLabel(result.type)}
                        </Badge>
                      </div>
                      {result.subtitle && (
                        <p className="text-xs text-muted-foreground truncate">
                          {result.subtitle}
                        </p>
                      )}
                    </div>

                    {isSelected && (
                      <ArrowRight className="w-4 h-4 text-primary-text shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/50 text-xs text-muted-foreground shrink-0">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="font-mono bg-background px-1.5 py-0.5 rounded border">↑↓</kbd>
              to navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="font-mono bg-background px-1.5 py-0.5 rounded border">↵</kbd>
              to select
            </span>
          </div>
          {query.trim().length >= 2 && !isLoading && !isError && (
            <span>{results.length} result{results.length === 1 ? "" : "s"}</span>
          )}
        </div>
      </DialogContent>
    </Dialog>
    <AddContactModal open={addContactOpen} onOpenChange={setAddContactOpen} />
    </>
  );
}
